import DOMPurify from 'dompurify';
import { ALLOWED_IFRAME_HOSTS, BASE_ALLOWED_TAGS, BASE_ALLOWED_ATTR, SAFE_URI_REGEXP } from './constants';

/**
 * Register the iframe host-allow-list hook exactly once (hooks are global
 * to the DOMPurify instance, not per-call). Any iframe whose src host is
 * not allow-listed — or whose src is non-https / unparseable — is removed.
 */
let hookRegistered = false;
function ensureIframeHostHook(): void {
  if (hookRegistered) return;
  hookRegistered = true;

  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') return;

    const el = node as Element;
    const rawSrc = el.getAttribute('src') || '';

    try {
      // Support protocol-relative Bilibili embeds: //player.bilibili.com/...
      const src = rawSrc.startsWith('//') ? new URL('https:' + rawSrc) : new URL(rawSrc, 'https://invalid.base');

      if (src.protocol !== 'https:' || !ALLOWED_IFRAME_HOSTS.has(src.hostname)) {
        el.parentNode?.removeChild(el);
      }
    } catch {
      el.parentNode?.removeChild(el);
    }
  });
}

ensureIframeHostHook();

export function sanitizeWidgetHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: BASE_ALLOWED_TAGS,
    ALLOWED_ATTR: BASE_ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
    FORBID_ATTR: ['srcdoc'],
  });
}

export function sanitizeWidgetVideo(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [...BASE_ALLOWED_TAGS, 'iframe'],
    ALLOWED_ATTR: [...BASE_ALLOWED_ATTR, 'allow', 'allowfullscreen', 'frameborder', 'scrolling', 'referrerpolicy'],
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
    FORBID_ATTR: ['srcdoc'],
  });
}

export function sanitizeWidget(html: string, type: string): string {
  return type === 'video' ? sanitizeWidgetVideo(html) : sanitizeWidgetHtml(html);
}
